import { motion } from "framer-motion";
import { HelpCircle, Mail } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import SEO from "@/components/SEO";
import { createFaqSchema, createBreadcrumbSchema } from "@/lib/jsonLd";

const faqs = [
  {
    question: "What packages do you offer?",
    answer: "We offer two packages: Basic Song ($35) which includes a personalized song with custom lyrics and music, and MP3 delivery. Premium Song + Slideshow ($85) includes everything in Basic plus a slideshow video with 15-40 of your photos, and MP4 video delivery with a digital lyric sheet."
  },
  {
    question: "How long does it take to create my custom song?",
    answer: "Standard delivery is 48-72 hours for both packages. Need it faster? Add Rush Delivery ($10) to get your song within 24 hours."
  },
  {
    question: "What information do I need to provide?",
    answer: "The more details you share, the more personal your song will be. We'll ask about the occasion, the recipient, your relationship, special memories, preferred musical style and mood, and any specific phrases you'd like included. Our simple form guides you through the process."
  },
  {
    question: "What if I don't like my song?",
    answer: "Your story matters to us. If your song doesn't feel quite right, let us know. We will review your feedback and work with you to make thoughtful adjustments so it reflects your vision as closely as possible. Our goal is for you to feel proud to share your song. If you have concerns, email us at song@yourmomentsong.com and we'll take care of you."
  },
  {
    question: "What musical styles are available?",
    answer: "We can do any style! Popular choices include Pop, Acoustic, Classical, Jazz, Country, R&B, Rock, Folk, and Electronic, but if you have something else in mind, just let us know what you'd like and we'll make it happen."
  },
  {
    question: "Who writes and performs the songs?",
    answer: "Our songs are written by our creative team, who thoughtfully craft each story based on the details you share. We use AI as a supportive tool in the production process to help bring each song to life efficiently. This approach allows us to create a truly personal, high quality song while keeping this meaningful gift accessible and reasonably priced for you."
  },
  {
    question: "What file formats will I receive?",
    answer: "Basic Song packages include a digital MP3 file. Premium Song + Slideshow packages include an MP4 video with your photos set to the music, plus a PDF digital lyric sheet."
  },
  {
    question: "How does the Premium slideshow work?",
    answer: "For Premium orders, you'll upload 15-40 photos that we'll use to create a professionally paced slideshow video matched to your custom song. All photos are kept private and used only for your video. Note: All images may not be used in final video."
  },
  {
    question: "Can I use my custom song for commercial purposes?",
    answer: "Your song is licensed for personal use, which includes playing at events, sharing with family and friends, and posting on personal social media. For commercial use (advertising, broadcasting, etc.), please contact us for a commercial license."
  },
  {
    question: "Can I give this as a surprise gift?",
    answer: "Yes! Many of our customers create songs as surprise gifts. We can coordinate delivery timing and even create a special presentation. Just let us know in your order notes."
  },
  {
    question: "How do I access my completed song?",
    answer: "Completed songs remain available for download in your account. You'll receive a notification when your song is ready, and you can access it anytime from your dashboard."
  }
];

const FAQ = () => {
  return (
    <div className="min-h-screen bg-background">
      <SEO
        title="FAQ — Custom Song Questions Answered"
        description="Frequently asked questions about custom songs, pricing, delivery times, revisions, and more at Your Moment Song."
        canonical="https://yourmomentsong.com/faq"
        url="https://yourmomentsong.com/faq"
        jsonLd={[
          createFaqSchema(faqs),
          createBreadcrumbSchema([
            { name: "Home", url: "https://yourmomentsong.com/" },
            { name: "FAQ", url: "https://yourmomentsong.com/faq" },
          ]),
        ]}
      />
      <Header />

      <main className="pt-24 pb-16">
        <div className="container mx-auto px-4 max-w-3xl">
          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-12"
          >
            <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-6">
              <HelpCircle className="w-8 h-8 text-primary" aria-hidden="true" />
            </div>
            <h1 className="font-heading text-4xl md:text-5xl font-bold text-foreground mb-4">
              Frequently Asked Questions
            </h1>
            <p className="text-lg text-muted-foreground">
              Everything you need to know about creating your personalized song.
            </p>
          </motion.div>

          {/* FAQ Accordion */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
          >
            <Accordion type="single" collapsible className="space-y-4">
              {faqs.map((faq, index) => (
                <AccordionItem
                  key={index}
                  value={`item-${index}`}
                  className="bg-card rounded-xl border border-border px-6 data-[state=open]:shadow-soft"
                >
                  <AccordionTrigger className="text-left font-heading font-semibold text-foreground hover:no-underline py-5">
                    {faq.question}
                  </AccordionTrigger>
                  <AccordionContent className="text-muted-foreground pb-5">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </motion.div>

          {/* Contact CTA */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mt-12 bg-card rounded-2xl p-8 shadow-soft"
          >
            <h3 className="font-heading text-xl font-semibold text-foreground mb-2">
              Still have questions?
            </h3>
            <p className="text-muted-foreground mb-6">
              We're here to help. Reach out to our team for personalized assistance.
            </p>
            <Button variant="hero" asChild>
              <Link to="/contact">
                <Mail className="w-5 h-5 mr-2" aria-hidden="true" />
                Contact Us
              </Link>
            </Button>
          </motion.div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default FAQ;
